import { IPosition, IPositionType, IPositionWithDrawing } from 'src/generated-types'
import {
  ConfigurationCard,
  DrawingGroupRow,
  PositionConfig,
  PositionTypeTab,
  QuickSelectMode,
  SelectedPositionLabel,
  ZoomRequest,
} from 'src/generated-types/configure-positions'
import { ZOOM_STEP, clampZoom, compareDrawingNumbers, isUngroupedName } from './configure/configurePositions.action'

export type SelectionByType = Record<number, number[]>

interface ConfigByIdSources {
  groupRows: DrawingGroupRow[]
  configurablePositions: IPositionWithDrawing[]
  drawingIdToNumber: Map<number, string>
  configuredColor: string
}

export const buildConfigById = ({
  groupRows,
  configurablePositions,
  drawingIdToNumber,
  configuredColor,
}: ConfigByIdSources) => {
  const configById = new Map<number, PositionConfig>()
  const addConfig = (
    positionId: number,
    drawingId: number,
    drawingNumber: string,
    groupId: number | null,
    groupName?: string,
  ) => {
    if (!drawingId) return
    configById.set(positionId, {
      drawingId,
      drawingNumber: drawingNumber || drawingIdToNumber.get(drawingId) || String(drawingId),
      groupId,
      groupName,
      color: configuredColor,
    })
  }

  groupRows.forEach(row => {
    const isGrouped = !isUngroupedName(row.group.name)
    row.group.positions?.forEach(position => {
      addConfig(
        position.id,
        row.drawingId,
        row.drawingNumber,
        isGrouped && row.group.id > 0 ? row.group.id : null,
        isGrouped ? row.group.name : undefined,
      )
    })
  })
  configurablePositions.forEach(position => {
    if (configById.has(position.id) || !position.positionDrawingId) return
    const isGrouped = !isUngroupedName(position.positionGroupsByDrawingName)
    addConfig(
      position.id,
      position.positionDrawingId,
      position.positionDrawingNumber ?? '',
      isGrouped ? position.positionGroupsByDrawingId ?? null : null,
      isGrouped ? position.positionGroupsByDrawingName : undefined,
    )
  })
  return configById
}

export const buildConfigurationCards = (positionsOfType: IPosition[], configById: Map<number, PositionConfig>) => {
  const cardsByKey = new Map<string, ConfigurationCard>()
  positionsOfType.forEach(position => {
    const config = configById.get(position.id)
    if (!config) return
    const key = `${config.drawingId}|${config.groupName ?? ''}`
    const card = cardsByKey.get(key) ?? { key, ...config, positions: [] }
    card.positions.push(position)
    cardsByKey.set(key, card)
  })
  return [...cardsByKey.values()].sort(compareDrawingNumbers)
}

export const findCardKeyForPosition = (cards: ConfigurationCard[], positionId: number | null) => {
  if (positionId == null) return null
  return cards.find(card => card.positions.some(position => position.id === positionId))?.key ?? null
}

export const buildTypeTabs = (
  types: IPositionType[],
  configurablePositions: IPosition[],
  configById: Map<number, PositionConfig>,
  selectedByType: SelectionByType,
  getTypeLabel: (type: IPositionType) => string,
): PositionTypeTab[] => {
  return types.map(type => {
    const positionsOfType = configurablePositions.filter(position => position.type === type)
    return {
      type,
      label: getTypeLabel(type),
      totalCount: positionsOfType.length,
      configuredCount: positionsOfType.filter(position => configById.has(position.id)).length,
      selectedCount: (selectedByType[type] ?? []).length,
    }
  })
}

export const toggleId = (selectedIds: number[], id: number) => {
  const nextIds = new Set(selectedIds)
  if (nextIds.has(id)) nextIds.delete(id)
  else nextIds.add(id)
  return [...nextIds]
}

export const addIds = (selectedIds: number[], ids: number[]) => {
  return [...new Set([...selectedIds, ...ids])]
}

export const removeIds = (selectedIds: number[], ids: number[]) => {
  return selectedIds.filter(id => !ids.includes(id))
}

export const toggleIdGroup = (selectedIds: number[], ids: number[]) => {
  const nextIds = new Set(selectedIds)
  const allSelected = ids.every(id => nextIds.has(id))
  ids.forEach(id => (allSelected ? nextIds.delete(id) : nextIds.add(id)))
  return [...nextIds]
}

export const getQuickSelectIds = (
  mode: QuickSelectMode,
  positionsOfType: IPosition[],
  configById: Map<number, PositionConfig>,
) => {
  if (mode === 'none') return []
  const candidates = mode === 'all' ? positionsOfType : positionsOfType.filter(position => !configById.has(position.id))
  return candidates.map(position => position.id)
}

export const getNextZoom = (currentZoom: number, request: ZoomRequest) => {
  if (request === 'fit') return 1
  if (typeof request === 'number') return clampZoom(request)
  return clampZoom(request === 'in' ? currentZoom * ZOOM_STEP : currentZoom / ZOOM_STEP)
}

interface RenameOnlyCheck {
  editingCard?: ConfigurationCard
  grouping: boolean
  pickedDrawingId: number | null
  selectedIds: Set<number>
  groupName: string
}

export const isRenameOnlyEdit = ({
  editingCard,
  grouping,
  pickedDrawingId,
  selectedIds,
  groupName,
}: RenameOnlyCheck) => {
  if (!editingCard || !grouping || pickedDrawingId !== editingCard.drawingId) return false
  const hasSameSelection =
    selectedIds.size === editingCard.positions.length &&
    editingCard.positions.every(position => selectedIds.has(position.id))
  return hasSameSelection && groupName.trim() !== (editingCard.groupName ?? '')
}

export const buildSuggestedGroupName = (typeLabel: string, selectedLabels: SelectedPositionLabel[]) => {
  if (!selectedLabels.length) return ''
  const firstLabel = selectedLabels[0].label
  const lastLabel = selectedLabels[selectedLabels.length - 1].label
  return `${typeLabel} ${firstLabel}${selectedLabels.length > 1 ? `–${lastLabel}` : ''}`
}
